/**
 * 全局弹框提示 - 基于 Ant Design Vue 的行业最佳实践配置
 * 统一消息提示、通知、确认框的调用方式
 */

import type { VNode } from 'vue'
import { message as AntMessage, Modal, notification } from 'ant-design-vue'
import type { ModalFuncProps } from 'ant-design-vue'

// 默认配置（行业常用：3秒、可关闭）
AntMessage.config({
  duration: 3,
  top: '24px',
})

const MSGBOX_DEFAULTS: Partial<ModalFuncProps> = {
  okText: '确定',
  cancelText: '取消',
}

const NOTIFY_DEFAULTS = {
  duration: 4.5,
  placement: 'topRight' as 'topRight' | 'topLeft' | 'bottomRight' | 'bottomLeft',
}

const createMsg =
  (type: 'success' | 'error' | 'warning' | 'info') =>
  (content: string, options?: { duration?: number }) =>
    AntMessage[type](content, options?.duration ?? 3)

/** 消息提示 */
export const msg = {
  success: createMsg('success'),
  error: createMsg('error'),
  warning: createMsg('warning'),
  info: createMsg('info'),
  closeAll: () => AntMessage.destroy(),
}

/** 确认框选项（支持按按钮类型执行不同回调） */
export interface MsgboxOptions extends Partial<ModalFuncProps> {
  /** 点击「确定」时回调，可返回 Promise 以在异步完成后关闭 */
  onOk?: (e?: MouseEvent) => void | Promise<void>
  /** 点击「取消」或关闭时回调 */
  onCancel?: (e?: MouseEvent) => void
}

type MsgboxContent = string | VNode

/** 确认框（支持 onOk / onCancel 点击回调，也可用返回的 Promise then/catch 区分） */
export const msgbox = {
  alert: (message: MsgboxContent, title = '提示', options?: MsgboxOptions) =>
    Modal.info({ title, content: message, ...MSGBOX_DEFAULTS, ...options }),

  confirm: (message: MsgboxContent, title = '确认', options?: MsgboxOptions) =>
    Modal.confirm({
      title,
      content: message,
      ...MSGBOX_DEFAULTS,
      okCancel: true,
      ...options,
    }),

  prompt: (message: MsgboxContent, title = '输入', options?: MsgboxOptions) =>
    Modal.confirm({
      title,
      content: message,
      ...MSGBOX_DEFAULTS,
      okCancel: true,
      ...options,
    }),
}

const NOTIFY_TITLES = { success: '成功', error: '错误', warning: '警告', info: '提示' } as const

const createNotify =
  (type: keyof typeof NOTIFY_TITLES) =>
  (
    content: string,
    title = '',
    options?: {
      duration?: number
      placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
    },
  ) =>
    notification[type]({
      ...NOTIFY_DEFAULTS,
      ...options,
      message: title || NOTIFY_TITLES[type],
      description: content,
    })

/** 通知 */
export const notify = {
  success: createNotify('success'),
  error: createNotify('error'),
  warning: createNotify('warning'),
  info: createNotify('info'),
}
